/**
 * UserList - User management
 *
 * Features:
 * - Table view with all users
 * - Create new user + FTP credential (manager only)
 * - Role assignment
 * - Active/inactive toggle
 * - Delete user
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import {
  fetchUsers,
  fetchRoles,
  updateUser,
  createUser,
  createFtpSource,
  updateFtpSource,
  fetchFtpSyncStatus,
} from '../../../../api';
import { useAuth } from '../../../../auth.jsx';
import Button from '../../../../shared/components/ui/Button';
import Modal from '../../../../shared/components/ui/Modal';
import Spinner from '../../../../shared/components/ui/Spinner';
import { Btn, Drawer } from '../../../../shared/components/redesign';
import styles from './UserList.module.css';

function SortHeader({ label, sortKeyName, sortKey, sortDir, onSort }) {
  return (
    <th
      className={clsx(styles.th, styles.sortable)}
      onClick={() => onSort(sortKeyName)}
    >
      {label}
      {sortKey === sortKeyName && (
        <span className={styles.sortIndicator}>{sortDir === 'asc' ? ' ^' : ' v'}</span>
      )}
    </th>
  );
}

const STATUS_FILTERS = [
  { key: 'all', label: 'All Users' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

const FTP_SYNC_STALE_MS = 5 * 60 * 1000;

function describeFtpSync(sync) {
  if (sync === undefined) return 'FTP sync: checking...';
  if (!sync || !sync.reported_at) {
    return 'FTP sync: no report yet, changes may not have reached the FTP server.';
  }
  const ageMs = Date.now() - new Date(sync.reported_at).getTime();
  if (Number.isNaN(ageMs) || ageMs > FTP_SYNC_STALE_MS) {
    return 'FTP sync: no report in the last 5 minutes, changes may not have reached the FTP server.';
  }
  const secs = Math.max(0, Math.round(ageMs / 1000));
  const parts = [`${sync.desired ?? 0} accounts`];
  if (sync.locked) parts.push(`${sync.locked} locked this pass`);
  if (sync.errors && sync.errors.length) parts.push(`${sync.errors.length} errors`);
  return `FTP sync: last run ${secs} s ago on ${sync.hostname || 'the FTP host'}, ${parts.join(', ')}.`;
}

function UserDrawer({ user, roles, onSave, onRefresh, onClose }) {
  const [form, setForm] = useState({
    name: user?.name || '',
    custom_role_id: user?.custom_role_id || roles[0]?.id || '',
    is_active: !!user?.is_active,
  });
  const [saving, setSaving] = useState(false);
  const [ftpBusy, setFtpBusy] = useState(false);
  const [ftpError, setFtpError] = useState(null);
  const [ftpSync, setFtpSync] = useState(undefined); // undefined = loading, null = no report

  const cred = user.ftp_credential;

  const loadFtpSync = useCallback(() => {
    fetchFtpSyncStatus()
      .then((data) => setFtpSync(data?.sync ?? null))
      .catch(() => setFtpSync(null));
  }, []);

  useEffect(() => {
    loadFtpSync();
  }, [loadFtpSync]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      console.error('Failed to save user:', err);
    } finally {
      setSaving(false);
    }
  };

  const runFtpAction = async (fn) => {
    setFtpBusy(true);
    setFtpError(null);
    try {
      await fn();
      await onRefresh();
    } catch (err) {
      setFtpError(err.response?.data?.detail || err.response?.data?.error || err.message);
    } finally {
      setFtpBusy(false);
      // The intake container applies the change within its next pass; look again shortly.
      setTimeout(loadFtpSync, 5000);
    }
  };

  const handleRotateFtp = () => {
    if (!cred) return;
    runFtpAction(() => updateFtpSource(cred.id, { rotate_password: true }));
  };

  const handleToggleFtpEnabled = (enabled) => {
    if (!cred) return;
    runFtpAction(() => updateFtpSource(cred.id, { enabled }));
  };

  const handleCreateFtp = () => {
    runFtpAction(() => createFtpSource({ user_id: user.id }));
  };

  const body = (
    <>
      <div className={styles.userInfo}>
        <span className={styles.userEmail}>{user.email}</span>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Name</label>
        <input
          type="text"
          className={styles.input}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="User name"
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Role</label>
        <select
          className={styles.select}
          value={form.custom_role_id}
          onChange={(e) => setForm({ ...form, custom_role_id: e.target.value })}
        >
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className={styles.checkbox}
          />
          <span>Active</span>
        </label>
        <p className={styles.hint}>
          Inactive users cannot log in but their data is preserved.
        </p>
      </div>

      <h3 className={styles.sectionHeading}>FTP credential</h3>
      <p className={styles.hint}>{describeFtpSync(ftpSync)}</p>

      {ftpError && (
        <div className={styles.error}><span>{ftpError}</span></div>
      )}

      {cred ? (
        <>
          <CredentialRow label="Username" value={cred.ftp_username} />
          <CredentialRow label="Password" value={cred.ftp_password} />

          <div className={styles.formGroup}>
            <Btn kind="ghost" size="sm" onClick={handleRotateFtp} disabled={ftpBusy}>
              {ftpBusy ? 'Working…' : 'Rotate password'}
            </Btn>
            <p className={styles.hint}>
              The new password reaches the FTP server within a minute. The old one stops working at the same moment.
            </p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={cred.enabled}
                onChange={(e) => handleToggleFtpEnabled(e.target.checked)}
                disabled={ftpBusy}
                className={styles.checkbox}
              />
              <span>FTP login enabled</span>
            </label>
            <p className={styles.hint}>
              Disabling blocks new logins and cuts any upload session this user has open, within a minute.
            </p>
          </div>
        </>
      ) : (
        <div className={styles.formGroup}>
          <p className={styles.hint}>
            This user has no FTP credential yet. Create one to allow camera uploads.
          </p>
          <Btn kind="primary" size="sm" onClick={handleCreateFtp} disabled={ftpBusy}>
            {ftpBusy ? 'Working…' : 'Create FTP credential'}
          </Btn>
        </div>
      )}
    </>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow="§ USER"
      title={user?.name || user?.email || 'User'}
      subtitle={user?.email && user?.name ? user.email : undefined}
      ariaLabel="Edit user"
      body={body}
      footer={(
        <>
          <Btn kind="ghost" size="sm" onClick={onClose}>Cancel</Btn>
          <span className={styles.footerSpacer} />
          <Btn kind="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Btn>
        </>
      )}
    />
  );
}

function CredentialRow({ label, value }) {
  const [revealed, setRevealed] = useState(false);
  const handleCopy = () => {
    navigator.clipboard?.writeText(value);
  };
  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>{label}</label>
      <div className={styles.credentialRow}>
        <input
          type={revealed ? 'text' : 'password'}
          className={styles.input}
          value={value}
          readOnly
        />
        <Btn kind="ghost" size="sm" onClick={() => setRevealed((r) => !r)}>
          {revealed ? 'Hide' : 'Show'}
        </Btn>
        <Btn kind="ghost" size="sm" onClick={handleCopy}>Copy</Btn>
      </div>
    </div>
  );
}

function NewUserDrawer({ roles, onCreate, onClose }) {
  const [form, setForm] = useState({
    email: '',
    name: '',
    custom_role_id: roles[0]?.id || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [credentials, setCredentials] = useState(null);

  const canSubmit = form.email.trim() && form.custom_role_id;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const data = await onCreate({
        email: form.email.trim(),
        name: form.name.trim(),
        role_id: form.custom_role_id,
      });
      setCredentials(data);
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.email?.[0] ||
        err.response?.data?.role_id?.[0] ||
        err.message;
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyAll = () => {
    if (!credentials) return;
    const text = [
      `Web username: ${credentials.user.username}`,
      `Email: ${credentials.user.email}`,
      `Web password: ${credentials.web_password}`,
      '',
      `FTP username: ${credentials.ftp.ftp_username}`,
      `FTP password: ${credentials.ftp.ftp_password}`,
    ].join('\n');
    navigator.clipboard?.writeText(text);
  };

  if (credentials) {
    const body = (
      <>
        <p className={styles.hint}>
          These passwords are shown only once. Copy them now and share them with the user out-of-band.
        </p>

        <h3 className={styles.sectionHeading}>Web login</h3>
        <CredentialRow label="Username" value={credentials.user.username} />
        <CredentialRow label="Email" value={credentials.user.email} />
        <CredentialRow label="Password" value={credentials.web_password} />

        <h3 className={styles.sectionHeading}>FTP credentials</h3>
        <CredentialRow label="Username" value={credentials.ftp.ftp_username} />
        <CredentialRow label="Password" value={credentials.ftp.ftp_password} />

        <div className={styles.formGroup}>
          <Btn kind="ghost" size="sm" onClick={handleCopyAll}>Copy all credentials</Btn>
        </div>
      </>
    );

    return (
      <Drawer
        open
        onClose={onClose}
        eyebrow="§ CREDENTIALS"
        title={credentials.user.name || credentials.user.email}
        subtitle="Save these now — they cannot be retrieved later"
        ariaLabel="New user credentials"
        body={body}
        footer={(
          <>
            <span className={styles.footerSpacer} />
            <Btn kind="primary" size="sm" onClick={onClose}>Done</Btn>
          </>
        )}
      />
    );
  }

  const body = (
    <>
      {error && (
        <div className={styles.error}><span>{error}</span></div>
      )}

      <div className={styles.formGroup}>
        <label className={styles.label}>Email</label>
        <input
          type="email"
          className={styles.input}
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="user@example.com"
          autoFocus
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Name (optional)</label>
        <input
          type="text"
          className={styles.input}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Full name"
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Role</label>
        <select
          className={styles.select}
          value={form.custom_role_id}
          onChange={(e) => setForm({ ...form, custom_role_id: e.target.value })}
        >
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
        <p className={styles.hint}>
          A web-login password and FTP credentials will be auto-generated and shown once.
        </p>
      </div>
    </>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow="§ NEW USER"
      title="Create user"
      subtitle="Account + FTP endpoint"
      ariaLabel="Create user"
      body={body}
      footer={(
        <>
          <Btn kind="ghost" size="sm" onClick={onClose}>Cancel</Btn>
          <span className={styles.footerSpacer} />
          <Btn kind="primary" size="sm" onClick={handleSubmit} disabled={saving || !canSubmit}>
            {saving ? 'Creating…' : 'Create user'}
          </Btn>
        </>
      )}
    />
  );
}

export default function UserList() {
  const { user: currentUser } = useAuth();
  const isManager = currentUser?.role === 'company_manager';

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  // Sorting
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Modals
  const [editingUserId, setEditingUserId] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  // Confirmation/alert modal state (replaces browser alert)
  const [successMessage, setSuccessMessage] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, rolesData] = await Promise.all([
        fetchUsers({ limit: 200 }),
        fetchRoles({ limit: 100 }),
      ]);
      setUsers(usersData.results || usersData || []);
      setRoles(rolesData.results || rolesData || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    let result = users;

    // Search filter
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (u) =>
          u.name?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter === 'active') {
      result = result.filter((u) => u.is_active);
    } else if (statusFilter === 'inactive') {
      result = result.filter((u) => !u.is_active);
    }

    // Role filter
    if (roleFilter !== 'all') {
      result = result.filter((u) => u.custom_role_id === roleFilter);
    }

    // Sort
    result = [...result].sort((a, b) => {
      const rawA = a[sortKey] || '';
      const rawB = b[sortKey] || '';
      const aVal = typeof rawA === 'string' ? rawA.toLowerCase() : rawA;
      const bVal = typeof rawB === 'string' ? rawB.toLowerCase() : rawB;

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [users, search, statusFilter, roleFilter, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const editingUser = useMemo(
    () => (editingUserId ? users.find((u) => u.id === editingUserId) : null),
    [users, editingUserId],
  );

  const handleEdit = (user) => {
    setEditingUserId(user.id);
  };

  const handleSaveUser = async (formData) => {
    if (!editingUserId) return;
    await updateUser(editingUserId, formData);
    loadData();
  };

  const handleCreateUser = async (payload) => {
    const data = await createUser(payload);
    loadData();
    return data;
  };

  const getRoleName = (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    return role?.name || 'Unknown';
  };

  if (loading && users.length === 0) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Users</h2>
          <p className={styles.subtitle}>
            Manage team members and their access permissions
          </p>
        </div>
        {isManager && (
          <Btn
            kind="primary"
            size="sm"
            onClick={() => setShowNewModal(true)}
            disabled={roles.length === 0}
          >
            + New User
          </Btn>
        )}
      </div>

      {error && (
        <div className={styles.error}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">All Roles</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>

        <span className={styles.count}>{filteredUsers.length} users</span>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <SortHeader label="Name" sortKeyName="name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Email" sortKeyName="email" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Role" sortKeyName="custom_role_id" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className={styles.th}>FTP</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} className={styles.row}>
                <td className={styles.td}>
                  <span className={styles.userName}>{user.name || '-'}</span>
                </td>
                <td className={styles.td}>
                  <span className={styles.userEmail}>{user.email}</span>
                </td>
                <td className={styles.td}>
                  <span className={styles.roleBadge}>{getRoleName(user.custom_role_id)}</span>
                </td>
                <td className={styles.td}>
                  {user.ftp_credential ? (
                    <span className={styles.ftpUsername} title={user.ftp_credential.ftp_username}>
                      {user.ftp_credential.ftp_username}
                      {user.ftp_credential.enabled === false && (
                        <span className={styles.ftpDisabled}> · disabled</span>
                      )}
                    </span>
                  ) : (
                    <span className={styles.ftpMissing}>—</span>
                  )}
                </td>
                <td className={styles.td}>
                  {user.is_active ? (
                    <span className={clsx(styles.statusBadge, styles.active)}>
                      Active
                    </span>
                  ) : (
                    <span className={clsx(styles.statusBadge, styles.inactive)}>
                      Inactive
                    </span>
                  )}
                </td>
                <td className={styles.td}>
                  <div className={styles.actions}>
                    <Btn kind="ghost" size="sm" onClick={() => handleEdit(user)}>
                      Edit
                    </Btn>
                  </div>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {editingUser && (
        <UserDrawer
          user={editingUser}
          roles={roles}
          onSave={handleSaveUser}
          onRefresh={loadData}
          onClose={() => setEditingUserId(null)}
        />
      )}

      {showNewModal && (
        <NewUserDrawer
          roles={roles}
          onCreate={handleCreateUser}
          onClose={() => setShowNewModal(false)}
        />
      )}

      {/* Success Message Modal (replaces browser alert) */}
      <Modal
        isOpen={!!successMessage}
        onClose={() => setSuccessMessage(null)}
        size="sm"
      >
        <Modal.Header onClose={() => setSuccessMessage(null)}>
          Success
        </Modal.Header>
        <Modal.Body>
          <p>{successMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setSuccessMessage(null)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
